import axios from '@/core/axios';

import { CollectionList } from '../collections/collections.types';
import {
  Customer,
  CustomerContact,
  CustomerCountry,
  CustomerOrder,
  CustomerStats,
  CustomersListOptions
} from './customers.types';

export class CustomersService {
  list(opts: CustomersListOptions = {}): Promise<CollectionList<'customers'>> {
    const { filters, pagination } = opts;
    return axios
      .get<CollectionList<'customers'>>('/customers', { params: { ...filters, ...pagination } })
      .then(resp => resp?.data);
  }

  stats(): Promise<CustomerStats> {
    return axios.get<CustomerStats>('/customers/stats').then(resp => resp?.data);
  }

  get(customerId: number | string): Promise<Customer> {
    return axios.get<Customer>(`/customers/${customerId}`).then(resp => resp?.data);
  }

  newCustomer(): Promise<Customer> {
    return axios.get<Customer>(`/customers/new`).then(resp => resp?.data);
  }

  create(customer: Partial<Customer>): Promise<Customer> {
    return axios.post<Customer>(`/customers`, { customer }).then(resp => resp?.data);
  }

  update(customerId: number | string, customer: Partial<Customer>): Promise<Customer> {
    if (!customerId) throw new Error('Missing customer id in update');
    return axios.put<Customer>(`/customers/${customerId}`, { customer }).then(resp => resp?.data);
  }

  delete(customerId: number | string): Promise<void> {
    if (!customerId) throw new Error('Missing customer id in delete');
    return axios.delete<void>(`/customers/${customerId}`).then(resp => resp?.data);
  }

  archive(customerId: number | string): Promise<Customer> {
    if (!customerId) throw new Error('Missing customer id in archive');
    return axios.post<Customer>(`/customers/${customerId}/archive`).then(resp => resp?.data);
  }

  block(customerId: number | string): Promise<{
    customer: Customer;
    blocking: {
      customer_id: number;
      customer_type: string;
      cancelled_order_lines_count: number;
      cancelled_orders_count: number;
      cancelled_order_line_ids: number[];
    };
  }> {
    if (!customerId) throw new Error('Missing customer id in block');
    return axios.post(`/customers/${customerId}/block`).then(resp => resp?.data);
  }

  unblock(customerId: number | string): Promise<Customer> {
    if (!customerId) throw new Error('Missing customer id in unblock');
    return axios.post<Customer>(`/customers/${customerId}/unblock`).then(resp => resp?.data);
  }

  unflagBadPayer(customerId: number | string): Promise<Customer> {
    if (!customerId) throw new Error('Missing customer id in unflagBadPayer');
    return axios.post<Customer>(`/customers/${customerId}/unflag-bad-payer`).then(resp => resp?.data);
  }

  favorite(customerId: number | string): Promise<Customer> {
    if (!customerId) throw new Error('Missing customer id in favorite');
    return axios.post<Customer>(`/customers/${customerId}/favorite`).then(resp => resp?.data);
  }

  paymentConditions(): Promise<string[]> {
    return axios.get<string[]>('/customers/payment-conditions').then(resp => resp?.data || []);
  }

  /* Commandes liées */
  listOrders(customerId: number | string): Promise<CustomerOrder[]> {
    return axios.get<CustomerOrder[]>(`/customers/${customerId}/orders`).then(resp => resp?.data || []);
  }

  linkOrder(customerId: number | string, marketplaceOrderId: string, force = false): Promise<CustomerOrder> {
    return axios
      .post<CustomerOrder>(`/customers/${customerId}/link_order`, {
        marketplace_order_id: marketplaceOrderId,
        force
      })
      .then(resp => resp?.data);
  }

  countries(): Promise<CustomerCountry[]> {
    return axios.get<CustomerCountry[]>('/customers/countries').then(resp => resp?.data || []);
  }

  /* Contacts */
  listContacts(customerId: number | string): Promise<CustomerContact[]> {
    return axios.get<CustomerContact[]>(`/customers/${customerId}/contacts`).then(resp => resp?.data || []);
  }

  createContact(customerId: number | string, contact: Partial<CustomerContact>): Promise<CustomerContact> {
    return axios
      .post<CustomerContact>(`/customers/${customerId}/contacts`, { customer_contact: contact })
      .then(resp => resp?.data);
  }

  updateContact(
    customerId: number | string,
    contactId: number | string,
    contact: Partial<CustomerContact>
  ): Promise<CustomerContact> {
    return axios
      .put<CustomerContact>(`/customers/${customerId}/contacts/${contactId}`, { customer_contact: contact })
      .then(resp => resp?.data);
  }

  deleteContact(customerId: number | string, contactId: number | string): Promise<void> {
    return axios.delete<void>(`/customers/${customerId}/contacts/${contactId}`).then(resp => resp?.data);
  }
}

export const customersService = new CustomersService();
